import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { Badge } from '@carouselgg/nexus/badge'
import { Button } from '@carouselgg/nexus/button'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { ChevronRight } from '@carouselgg/nexus/icon'
import { LogoMarkWhite } from '@carouselgg/nexus/brand'
import { listPlatformArticles } from '../api/content'
import type { PlatformArticle } from '../api/content'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import styles from './LatestNews.module.css'

// Branded fallback covers for articles without an uploaded image — varied by
// index so a row of cards never shows three identical gradients.
const COVER_GRADIENTS = [
  'linear-gradient(135deg, var(--nexus-purple), var(--nexus-pink))',
  'linear-gradient(135deg, var(--nexus-blue), var(--nexus-green))',
  'linear-gradient(135deg, var(--nexus-pink), var(--nexus-purple), var(--nexus-blue))',
]

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

/**
 * Marketing-styled "Latest News" section for the homepage. Fetches the latest
 * platform articles and renders nothing when there are none (so the page never
 * shows an empty shell — important pre-launch). The full archive lives at /news.
 */
export function LatestNews() {
  const [articles, setArticles] = useState<PlatformArticle[]>([])

  const load = useCallback(async () => {
    try {
      const data = await listPlatformArticles(3, 0)
      setArticles(data.articles)
    } catch {
      // API unavailable — section stays hidden
    }
  }, [])

  useEffect(() => { load() }, [load])

  if (articles.length === 0) return null

  return (
    <section id="news" className={styles.section}>
      <ScrollReveal>
        <h2 className={styles.heading}>
          <GradientText from="var(--nexus-purple)" to="var(--nexus-pink)">
            Latest from Carousel
          </GradientText>
        </h2>
        <Text as="p" size="lg" muted className={styles.subheading}>
          Product updates, features, and community news — we build in public.
        </Text>
      </ScrollReveal>

      <div className={styles.grid}>
        {articles.map((article, i) => (
          <ScrollReveal key={article.id} delay={i * 100} className={styles.cardWrap}>
            <Link to={`/news/${article.slug}`} className={styles.articleLink}>
              <Card interactive accent="var(--nexus-purple)" className={styles.card}>
                <div className={styles.cardCover}>
                  {article.cover_image_url ? (
                    <img src={article.cover_image_url} alt="" />
                  ) : (
                    <div
                      className={styles.coverPlaceholder}
                      style={{ background: COVER_GRADIENTS[i % COVER_GRADIENTS.length] }}
                    >
                      <img src={LogoMarkWhite} alt="" className={styles.placeholderMark} />
                    </div>
                  )}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardMeta}>
                    {article.tags[0] && <Badge variant="default" size="sm">{article.tags[0]}</Badge>}
                    <Text size="xs" muted>{formatDate(article.published_at)}</Text>
                  </div>
                  <Text as="h3" size="base" weight="semibold">{article.title}</Text>
                  <Text size="sm" muted>{article.excerpt}</Text>
                </div>
              </Card>
            </Link>
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal>
        <div className={styles.viewAll}>
          <Link to="/news" style={{ textDecoration: 'none' }}>
            <Button variant="ghost" size="sm">View all news <ChevronRight size={14} /></Button>
          </Link>
        </div>
      </ScrollReveal>
    </section>
  )
}
